import { Color, rgb, unpackRgb } from '../core/types';

/**
 * The standard xterm palette.
 *
 * 0-15   system colours (the usual xterm defaults)
 * 16-231 6x6x6 colour cube
 * 232-255 24-step greyscale ramp
 *
 * The document always stores truecolor; these tables exist so that export and
 * preview can quantise, and so that imported indexed colours land on exactly
 * the RGB values that quantise back to the same index (lossless round-trip).
 */

export const ANSI16: readonly Color[] = [
  0x000000, 0x800000, 0x008000, 0x808000, 0x000080, 0x800080, 0x008080, 0xc0c0c0,
  0x808080, 0xff0000, 0x00ff00, 0xffff00, 0x0000ff, 0xff00ff, 0x00ffff, 0xffffff,
];

export const ANSI16_NAMES: readonly string[] = [
  'black', 'red', 'green', 'yellow', 'blue', 'magenta', 'cyan', 'white',
  'bright black', 'bright red', 'bright green', 'bright yellow',
  'bright blue', 'bright magenta', 'bright cyan', 'bright white',
];

const CUBE_STEPS = [0, 95, 135, 175, 215, 255];

function buildAnsi256(): Color[] {
  const table: Color[] = [...ANSI16];
  for (let r = 0; r < 6; r++) {
    for (let g = 0; g < 6; g++) {
      for (let b = 0; b < 6; b++) {
        table.push(rgb(CUBE_STEPS[r], CUBE_STEPS[g], CUBE_STEPS[b]));
      }
    }
  }
  for (let i = 0; i < 24; i++) {
    const v = 8 + i * 10;
    table.push(rgb(v, v, v));
  }
  return table;
}

export const ANSI256: readonly Color[] = buildAnsi256();

/** Perceptually weighted squared distance (cheap, no gamma round-trip). */
export function colorDistance(a: Color, b: Color): number {
  const [ar, ag, ab] = unpackRgb(a);
  const [br, bg, bb] = unpackRgb(b);
  const rMean = (ar + br) / 2;
  const dr = ar - br;
  const dg = ag - bg;
  const db = ab - bb;
  return (((512 + rMean) * dr * dr) >> 8) + 4 * dg * dg + (((767 - rMean) * db * db) >> 8);
}

function nearestIn(table: readonly Color[], color: Color, limit = table.length): number {
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < limit; i++) {
    const d = colorDistance(table[i], color);
    if (d < bestDist) {
      bestDist = d;
      best = i;
      if (d === 0) break;
    }
  }
  return best;
}

export function nearestAnsi16(color: Color): number {
  return nearestIn(ANSI16, color);
}

/**
 * Nearest 256-colour index. The cube/greyscale ramps are solved analytically
 * and then compared against the exact system colours, which is both faster and
 * more accurate than scanning all 256 entries.
 */
export function nearestAnsi256(color: Color): number {
  const [r, g, b] = unpackRgb(color);

  const cubeIndexFor = (v: number): number => {
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < 6; i++) {
      const d = Math.abs(CUBE_STEPS[i] - v);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    return best;
  };

  const ri = cubeIndexFor(r);
  const gi = cubeIndexFor(g);
  const bi = cubeIndexFor(b);
  const cubeIdx = 16 + 36 * ri + 6 * gi + bi;
  let bestIdx = cubeIdx;
  let bestDist = colorDistance(ANSI256[cubeIdx], color);

  const grey = Math.round((r + g + b) / 3);
  const greyStep = Math.max(0, Math.min(23, Math.round((grey - 8) / 10)));
  const greyIdx = 232 + greyStep;
  const greyDist = colorDistance(ANSI256[greyIdx], color);
  if (greyDist < bestDist) {
    bestDist = greyDist;
    bestIdx = greyIdx;
  }

  // Ties go to the system colour: several RGB values exist twice in the table
  // (black is both 0 and 16), and the low index degrades better to ANSI16.
  const sysIdx = nearestIn(ANSI16, color);
  if (colorDistance(ANSI16[sysIdx], color) <= bestDist) bestIdx = sysIdx;

  return bestIdx;
}

export function ansi256ToRgb(index: number): Color {
  return ANSI256[Math.max(0, Math.min(255, index))];
}

export function ansi16ToRgb(index: number): Color {
  return ANSI16[Math.max(0, Math.min(15, index))];
}

/** Snaps a colour to what the given mode can actually display. */
export function quantize(color: Color, mode: 'ansi16' | 'ansi256' | 'truecolor'): Color {
  if (color < 0) return color;
  if (mode === 'truecolor') return color;
  if (mode === 'ansi16') return ansi16ToRgb(nearestAnsi16(color));
  return ansi256ToRgb(nearestAnsi256(color));
}

export interface ColorCompatibility {
  hex: string;
  truecolor: Color;
  ansi256: number;
  ansi256Hex: string;
  ansi16: number;
  ansi16Name: string;
  ansi16Hex: string;
  /** True when the colour survives the mode unchanged. */
  exactIn256: boolean;
  exactIn16: boolean;
}

/** Everything the colour picker needs to show per-mode fidelity. */
export function describeColor(color: Color): ColorCompatibility {
  const i256 = nearestAnsi256(color);
  const i16 = nearestAnsi16(color);
  const hex = (c: Color) => '#' + (c & 0xffffff).toString(16).padStart(6, '0');
  return {
    hex: hex(color),
    truecolor: color,
    ansi256: i256,
    ansi256Hex: hex(ANSI256[i256]),
    ansi16: i16,
    ansi16Name: ANSI16_NAMES[i16],
    ansi16Hex: hex(ANSI16[i16]),
    exactIn256: ANSI256[i256] === color,
    exactIn16: ANSI16[i16] === color,
  };
}

export interface NamedPalette {
  name: string;
  colors: string[];
}

/** Starter palettes. Users can add their own; none of these are required. */
export const BUILTIN_PALETTES: readonly NamedPalette[] = [
  {
    name: 'ANSI 16',
    colors: ANSI16.map((c) => '#' + c.toString(16).padStart(6, '0')),
  },
  {
    name: 'Monochrome Green',
    colors: ['#000000', '#0a2a12', '#11421d', '#1b6a2e', '#249142', '#32ff94', '#8dffc4', '#ffffff'],
  },
  {
    name: 'Dracula',
    colors: ['#282a36', '#44475a', '#f8f8f2', '#6272a4', '#8be9fd', '#50fa7b', '#ffb86c', '#ff79c6', '#bd93f9', '#ff5555', '#f1fa8c'],
  },
  {
    name: 'Nord',
    colors: ['#2e3440', '#3b4252', '#434c5e', '#4c566a', '#d8dee9', '#e5e9f0', '#eceff4', '#8fbcbb', '#88c0d0', '#81a1c1', '#5e81ac', '#bf616a', '#d08770', '#ebcb8b', '#a3be8c', '#b48ead'],
  },
  {
    name: 'Gruvbox',
    colors: ['#282828', '#cc241d', '#98971a', '#d79921', '#458588', '#b16286', '#689d6a', '#a89984', '#928374', '#fb4934', '#b8bb26', '#fabd2f', '#83a598', '#d3869b', '#8ec07c', '#ebdbb2'],
  },
  {
    name: 'Tokyo Night',
    colors: ['#1a1b26', '#24283b', '#414868', '#565f89', '#a9b1d6', '#c0caf5', '#7aa2f7', '#7dcfff', '#bb9af7', '#9ece6a', '#e0af68', '#f7768e'],
  },
];
